import React, { useEffect, useState, useRef } from 'react'
import { FormControl, FormLabel } from '@material-ui/core'
import InputLabel from '@material-ui/core/InputLabel'
import { IPhoto } from '../../models/Checklist'
import DescriptionIcon from '@material-ui/icons/Description'
import { PhotoThumbnail } from '../photos/PhotoThumbnail'
import {
  withOneplaceLibraryContext,
  IOneplaceLibraryContextProp
} from '../OneplaceLibraryProvider'
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles'

const styles = () =>
  createStyles({
    item: {
      width: '100%',
      marginTop: '10px'
    },
    fileName: {
      marginLeft: '5px',
      marginRight: '5px',
      verticalAlign: 'bottom',
      whiteSpace: 'nowrap'
    },
    icon: {
      verticalAlign: 'bottom',
      fontSize: 'medium'
    }
  })

export interface IAttachmentsFieldProps
  extends IOneplaceLibraryContextProp,
    WithStyles<typeof styles> {
  attachments: IPhoto[]
  onAttachmentClicked: (attachment: IPhoto) => void
  label?: string
  showLabel?: boolean
}

const showFileName = (filename: string, maxCharSize: number) => {
  if (maxCharSize === 0) {
    return '...'
  }
  if (filename.length <= maxCharSize) {
    return filename
  }
  return filename.substring(0, maxCharSize) + '...'
}

const getMaxCharSize = (ref: any) => {
  const divSize = ref.current ? ref.current.offsetWidth : 0
  return divSize / 10
}

export const AttachmentsField = withOneplaceLibraryContext(
  withStyles(styles)((props: IAttachmentsFieldProps) => {
    const ref = useRef(null)
    const [maxCharSize, setMaxCharSize] = useState<number | null>(null)
    const showLabel = props.showLabel !== false
    const attachmentItems = props.attachments
      ? props.attachments.filter(
          (p) =>
            p.attachmentType === 'attachment' || p.attachmentType === 'video'
        )
      : []

    useEffect(() => {
      setMaxCharSize(getMaxCharSize(ref))
    }, [])

    const hasVideos = attachmentItems.some((a) =>
      a.contentType?.startsWith('video/')
    )
    const baseLabel = props.label || props.ctx.i18next.t('attachments') || ''
    const label = baseLabel.charAt(0).toUpperCase() + baseLabel.slice(1)
    const pluralLabel = props.label
      ? label
      : attachmentItems.length !== 1 && !label.endsWith('s')
        ? label + 's'
        : label

    return (
      <FormControl fullWidth style={{ marginBottom: 10 }}>
        {showLabel && (
          <InputLabel shrink={true}>
            {pluralLabel} ({attachmentItems.length})
          </InputLabel>
        )}
        <div
          style={{
            marginTop: showLabel ? 15 : 0,
            display: hasVideos ? 'flex' : undefined,
            flexWrap: hasVideos ? 'wrap' : undefined
          }}
          ref={ref}
        >
          {attachmentItems.map((attachment: IPhoto, idx: number) => {
            const isVideo = attachment.contentType?.startsWith('video/')
            if (isVideo) {
              const videoUrl = props.ctx.client.getAuthenticatedPhotoUrl(
                attachment.localThumbnaiObjectlUrl ||
                  attachment.thumbnailUrl ||
                  attachment.url
              )
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    maxWidth: 68
                  }}
                >
                  <PhotoThumbnail
                    src={videoUrl}
                    photo={attachment}
                    onClick={() => props.onAttachmentClicked(attachment)}
                  />
                  {attachment.fileName && (
                    <span
                      title={attachment.fileName}
                      style={{
                        width: 60,
                        fontSize: 11,
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginTop: -2,
                        marginBottom: 4
                      }}
                    >
                      {attachment.fileName}
                    </span>
                  )}
                </div>
              )
            }
            return (
              <div key={idx} className={props.classes.item}>
                <FormLabel role='listitem' style={{ display: 'inline-flex' }}>
                  <DescriptionIcon className={props.classes.icon} />
                  <span
                    title='Attachment File Name'
                    className={props.classes.fileName}
                    style={{
                      cursor: attachment.url ? 'pointer' : ''
                    }}
                    onClick={() => props.onAttachmentClicked(attachment)}
                  >
                    {maxCharSize &&
                      showFileName(attachment.fileName, maxCharSize)}
                  </span>
                </FormLabel>
              </div>
            )
          })}
        </div>
      </FormControl>
    )
  })
)
